import { createRouter, Link, useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { useEffect, useState } from "react";
import { routeTree } from "./routeTree.gen";

interface User {
  id: string;
  name: string;
  email: string;
  image: string | null;
}

interface Session {
  id: string;
  userId: string;
  token: string;
  expiresAt: Date;
}

export interface RouterContext {
  auth: {
    user: User;
    session: Session;
    orgId: string | null;
    onboarded: boolean;
    isManager: boolean;
    isSuperadmin: boolean;
  } | null;
  isPending: boolean;
}

function NotFound() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [countdown, setCountdown] = useState(5);

  useEffect(() => {
    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          navigate({ to: "/" });
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [navigate]);

  return (
    <div className="flex flex-col items-center justify-center min-h-screen px-4 text-center">
      <h1 className="text-6xl font-bold text-primary">404</h1>
      <h2 className="mt-4 text-2xl font-semibold text-foreground">
        {t("notFound.title")}
      </h2>
      <p className="mt-2 text-muted-foreground">
        {t("notFound.message")}
      </p>
      <p className="mt-4 text-sm text-muted-foreground">
        {t("notFound.redirecting", { seconds: countdown })}
      </p>
      <Link
        to="/"
        className="mt-6 inline-flex items-center justify-center rounded-sm bg-primary hover:bg-primary-hover px-6 py-2.5 text-sm font-semibold tracking-[1px] uppercase text-surface transition-all duration-300 shadow-sm hover:shadow-lg"
      >
        {t("notFound.backHome")}
      </Link>
    </div>
  );
}

export const router = createRouter({
  routeTree,
  context: { auth: null, isPending: true },
  defaultNotFoundComponent: NotFound,
  defaultViewTransition: true,
});

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}
